// Focus mode helper utilities
// These small helpers keep our main focus mode component easy to read.
// They handle simple data transformations like date handling and text formatting
// so non-technical teammates can tweak the UI without digging through complex logic.

import { TaskWithContext, Task } from '../../lib/db';

export type TaskQueueCategory = 'overdue' | 'regular' | 'deliverable';

// Priority ladder so we can sort high-importance work first.
const PRIORITY_ORDER: Record<string, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3
};

export const priorityRank = (priority?: Task['priority']): number => {
  if (!priority) return PRIORITY_ORDER.Medium;
  return PRIORITY_ORDER[priority] ?? PRIORITY_ORDER.Medium;
};

// We normalise date inputs so string, Date, or null values can be compared safely.
export const normalizeDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getTime());
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Regular tasks have slightly different scheduling fields, so we capture the most relevant due date.
export const getRegularDueDate = (task: TaskWithContext): Date | null => {
  return normalizeDate(task.next_due_date || task.start_date || task.deadline);
};

// Deliverables follow the goal deadline first, then fall back to a task-level deadline if needed.
export const getDeliverableDeadline = (task: TaskWithContext): Date | null => {
  return normalizeDate(task.goal?.deadline || task.deadline);
};

// Build a simple string key for lookups in our estimate map (title + description keeps it human readable).
export const buildEstimateKey = (task: TaskWithContext): string => {
  return `${task.title}|${task.description || ''}`;
};

// Two formatters so we can display time estimates in short (queue) and long (summary) text.
export const formatMinutesShort = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours}h ${remaining}m` : `${hours}h`;
};

export const formatMinutesLong = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  const hourLabel = `${hours} hour${hours === 1 ? '' : 's'}`;
  if (!remaining) {
    return hourLabel;
  }
  const minuteLabel = `${remaining} minute${remaining === 1 ? '' : 's'}`;
  return `${hourLabel} ${minuteLabel}`;
};
